/* eslint-disable react/jsx-props-no-spreading */
import type React from 'react';

import { useIsMobile } from '../../../../../common/components/atoms/ResponsiveBreakpoints';
import Heading from '../../../../../common/components/Heading';
import { useAppSelector } from '../../../../../common/hooks/useThunkDispatch';
import type { PriceType } from '../../../../../common/models/priceData/priceData';
import { featureFlagsSelectors } from '../../../../../common/selectors/featureFlagsSelectors';
import ProductDetailsAccordion from '../ProductDetailsAccordion';
import type { ProductDetailsData } from '../ProductDetailsPage.types';
import styles from './ProductDetails.module.scss';
import ProductDetailsDescription from './ProductDetailsDescription';
import ProductDetailsLabels from './ProductDetailsLabels';
import ProductDetailsNicotineInformation from './ProductDetailsNicotineInformation';

interface ProductDetailsProps {
    product: ProductDetailsData;
    priceType: PriceType;
    aboveAddToCartSlot?: React.ReactNode;
    addToCartSlot?: React.ReactNode;
    sustainabilityButtonSlot?: React.ReactNode;
    productImageSlot?: React.ReactNode;
}

const ProductDetails = (props: ProductDetailsProps) => {
    const isMobile = useIsMobile();
    const isPILPricesLogicEnabled = useAppSelector(
        featureFlagsSelectors.selectPILPricesLogicEnabled,
    );
    return (
        <article className={styles.Container}>
            <div className={styles.Content}>
                <div className={styles.Media}>
                    <div className={styles.Picture}>{props.productImageSlot}</div>
                    {isMobile && (
                        <div className={styles.LabelsContainer}>
                            <ProductDetailsLabels product={props.product} orientation="vertical" />
                        </div>
                    )}
                </div>
                <div className={styles.Details}>
                    <div className={styles.DetailsContent}>
                        <div className={styles.Top}>
                            <Heading element="h1" alignment="start" className={styles.Heading}>
                                {props.product.name}
                            </Heading>
                            <ProductDetailsDescription
                                fromSweden={props.product.fromSweden}
                                manufacturer={props.product.manufacturer}
                                packageSizeInformation={props.product.packageSizeInformation}
                                consumerInformationText={
                                    props.product.consumerInformationText || []
                                }
                                isPharmaceutical={props.product.isPharmaceutical}
                            />
                            {!isMobile && (
                                <ProductDetailsLabels
                                    product={props.product}
                                    orientation="horizontal"
                                />
                            )}
                        </div>
                        {props.aboveAddToCartSlot}
                        <div className={styles.AddToCartContainer}>{props.addToCartSlot}</div>
                        <div className={styles.Bottom}>
                            {props.product.isNicotineProduct && (
                                <ProductDetailsNicotineInformation />
                            )}
                            {props.sustainabilityButtonSlot}
                            <ProductDetailsAccordion
                                defaultValue={!isPILPricesLogicEnabled ? 'priceHistory' : undefined} // always intially open priceHistory, business requirement
                                nutrientInformation={props.product?.nutrientInformation}
                                nutritionFacts={props.product?.nutritionFacts}
                                listOfIngredients={props.product?.listOfIngredients}
                                listOfNonFoodIngredients={props.product?.listOfNonFoodIngredients}
                                maxStorageTemperature={props.product?.maxStorageTemperature}
                                declarationOfOrigin={props.product?.declarationOfOrigin}
                                catchData={props.product?.catchData}
                                fishReportingInformations={props.product?.fishReportingInformations}
                                storageState={props.product?.storageState}
                                countriesOfOrigin={props.product?.countriesOfOrigin}
                                eanCode={props.product?.eanCode}
                                description={props.product?.description}
                                isMagazine={props.product?.isMagazine}
                                // for now always use price with tax
                                priceHistory={props.product?.historicalPriceData}
                                compulsoryAdditiveLabel={props.product?.compulsoryAdditiveLabel}
                                consumerInstructions={props.product?.consumerInstructions}
                                replacementCountries={props.product?.replacementCountries}
                                preparationInstructionsList={
                                    props.product?.preparationInstructionsList || []
                                }
                                healthattributeLabels={props.product?.healthattributeLabels}
                                healthSafetyPrecautionaryStatementText={
                                    props.product?.healthSafetyPrecautionaryStatementText
                                }
                                healthSafetySignalStatementText={
                                    props.product?.healthSafetySignalStatementText
                                }
                                healthSafetySignalWordsCode={
                                    props.product?.healthSafetySignalWordsCode
                                }
                                priceType={props.priceType}
                                isPharmaceutical={props.product?.isPharmaceutical}
                                regulatedArticleDescription={
                                    props.product?.regulatedArticleDescription ||
                                    props.product?.regulatedProductName
                                }
                                animalFoodData={props.product?.animalFoodData}
                                contactInformation={props.product?.contactInformations}
                                claimReportingInformations={
                                    props.product.claimReportingInformations
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
};

export default ProductDetails;
